/**
 * Pure derivation for the vitals hero (DIRECTION.md §1.1) — wraps
 * `summaryStrip.ts`'s `buildSummaryState` (kept as-is, with its own tested
 * behavior and call sites) and adds what the hero needs on top of it:
 *
 * - A four-state `status` (awake/sleeping/stalled/unknown) instead of the
 *   three-state `alive`. "Stalled" is new: the organism's session evidence
 *   says it should be awake, but neither a `brain.wake` nor a
 *   `runtime.heartbeat` has landed in the last ~2 decision cycles — a real,
 *   distinct problem from "asleep" (expected quiet) or "unknown" (no
 *   evidence at all, never guessed).
 * - `navSeries`: the equity curve's NAV values, chronological, for the
 *   inline sparkline — reads a field every other panel already fetches
 *   (`role.equityCurve()`), no new snapshot field.
 * - `dayPnl`: NAV now minus the first equity-curve row of the current UTC
 *   day — a real, derived number (never fabricated) from data already on
 *   screen elsewhere (the equity curve chart), labelled as derived in its
 *   tooltip by the DOM layer. `null` when the curve doesn't span a day
 *   boundary yet (never a guessed $0).
 * - `grossExposure`/`grossExposureCap`: R3 ("observatory visibility of
 *   smoother state") — the book's total gross exposure and its cap, read
 *   straight from `index.json.refs.sizing` (`SizingDocument.gross_current_
 *   weight`/`gross_cap_weight`); both `null` until this role has recorded at
 *   least one `sizing.smoother_state` event.
 */

import type { LogEvent } from "../types/events";
import type { EquityCurveRow } from "../types/snapshot";
import type { PerformanceDocument, SizingDocument } from "../types/incremental";
import { buildSummaryState, type SummaryState } from "./summaryStrip";
import { latestByKind } from "../brain/sessionStatus";
import { decisionCadenceMs } from "../format/cadence";
import { formatCount } from "../format/number";

export type VitalsStatus = "awake" | "sleeping" | "stalled" | "stopped" | "unknown";

export interface VitalsState extends SummaryState {
  readonly status: VitalsStatus;
  /** OQA: a stopped organism whose published tree is a fresh episode's
   * event-less stub (`index.json.episode`) — flat at its opening capital. */
  readonly freshEpisode: boolean;
  readonly navSeries: readonly number[];
  readonly dayPnl: number | null;
  readonly grossExposure: number | null;
  readonly grossExposureCap: number | null;
}

const STALL_CYCLES = 2;

/** OQA (2026-10-02): the floor under the stall window. Events reach the page
 * through the publisher (every 60s on VPS4), the rsync to the landing host and
 * this page's 15s poll, so the newest wake a reader can see is routinely 60-100s
 * old. Two 30s female cadences (60s) made a healthy organism flap to "Stalled"
 * on most polls; a real stall still shows within minutes. */
const PUBLISH_LATENCY_FLOOR_MS = 3 * 60_000;

function parseTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** UTC calendar-day key, matching `src/log/logFormat.ts`'s `dayKey` — used
 * to find "the first equity-curve row of today" for `dayPnl`. */
function utcDayKey(iso: string): string | null {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString().slice(0, 10);
}

/** Exported for `FleetScreen.ts`'s per-organism card (orchestrator course
 * correction, 2026-09-28: "day P&L" on each fleet card and in the fleet
 * summary header) — same derivation, not a second copy. */
export function computeDayPnl(curve: readonly EquityCurveRow[]): number | null {
  if (curve.length === 0) return null;
  const latest = curve[curve.length - 1]!;
  const latestNavRaw = latest.nav !== null ? Number.parseFloat(latest.nav) : null;
  // `Number.isFinite` here (not just `!== null`): a malformed nav string
  // parses to NaN, which is `!== null` and would otherwise slip past the
  // guard below and propagate as a NaN day-P&L — this module's own rule is
  // "null when it can't be honestly computed, never a guessed/garbage
  // value."
  const latestNav = latestNavRaw !== null && Number.isFinite(latestNavRaw) ? latestNavRaw : null;
  const todayKey = utcDayKey(latest.event_time);
  if (latestNav === null || todayKey === null) return null;

  const firstOfToday = curve.find((row) => utcDayKey(row.event_time) === todayKey);
  if (!firstOfToday || firstOfToday.nav === null) return null;
  const openNav = Number.parseFloat(firstOfToday.nav);
  if (!Number.isFinite(openNav)) return null;
  return latestNav - openNav;
}

/** "1 position · 0 open orders" — plain words, not a bare comma-joined pair
 * of numbers (2026-09-28 fix, exported for `VitalsHero.ts`'s
 * `positionsStat`: verified live, the hero used to read "1, 0 open orders",
 * which reads as a typo rather than a sentence). `formatCount` already
 * renders the honest "–" for a null count; the plural default ("positions"/
 * "orders") for that case is a neutral fallback, not a guess at the real
 * (unknown) count. */
export function formatPositionsAndOrders(
  openPositions: number | null,
  openOrders: number | null,
): string {
  const positionsText = `${formatCount(openPositions)} ${openPositions === 1 ? "position" : "positions"}`;
  const ordersText = `${formatCount(openOrders)} open ${openOrders === 1 ? "order" : "orders"}`;
  return `${positionsText} · ${ordersText}`;
}

function computeStatus(
  base: SummaryState,
  events: readonly LogEvent[],
  role: string,
  nowMs: number,
): VitalsStatus {
  if (base.alive !== "awake") {
    // "asleep" -> sleeping (expected quiet, never flagged stalled);
    // "unknown" (no session/wake evidence at all) stays unknown.
    return base.alive === "asleep" ? "sleeping" : "unknown";
  }
  const latestWakeMs = parseTime(base.lastDecisionEventTime);
  const latestHeartbeat = latestByKind(events, "runtime.heartbeat");
  const latestHeartbeatMs = parseTime(latestHeartbeat?.eventTime ?? null);
  const latestEvidenceMs = Math.max(latestWakeMs ?? -Infinity, latestHeartbeatMs ?? -Infinity);
  if (!Number.isFinite(latestEvidenceMs)) return "unknown"; // says awake but no timestamped evidence at all
  const cadence = decisionCadenceMs(role);
  const age = nowMs - latestEvidenceMs;
  return age > Math.max(cadence * STALL_CYCLES, PUBLISH_LATENCY_FLOOR_MS) ? "stalled" : "awake";
}

export interface BuildVitalsStateOptions {
  /** R3 ("observatory visibility of smoother state") — `index.json.refs.
   * sizing`'s gross-exposure fields, when known. `undefined`/`null` (the
   * default) renders `grossExposure`/`grossExposureCap` as `null`, same as
   * a role that has never recorded a `sizing.smoother_state` event. */
  readonly sizing?: Pick<SizingDocument, "gross_current_weight" | "gross_cap_weight"> | null;
  /** Defaults to `Date.now()`; overridable for deterministic tests. */
  readonly nowMs?: number;
  /** OQA: fleet.json's `unit_state` for this organism (the systemd word). Any
   * value other than "active" makes the status "stopped" — the organism is not
   * running, whatever its last published events say. `null`/absent: unknown
   * (pre-OQA fleet.json), judged from events alone as before. */
  readonly unitState?: string | null;
  /** OQA: `index.json.episode.status === "pending_first_wake"`. */
  readonly freshEpisode?: boolean;
}

export function buildVitalsState(
  events: readonly LogEvent[],
  performance: Pick<PerformanceDocument, "nav" | "unrealized_pnl" | "realized_pnl_cum"> | null,
  equityCurve: readonly EquityCurveRow[],
  // The canonical, closed-set role ("male_equity"/"female_crypto" —
  // `RoleLocation.role`, e.g. `VitalsHero.ts`'s `this.role.location.role`),
  // not an organism_id — named for what it actually is (code-review
  // finding: this parameter used to be called `organismId` despite every
  // real caller passing `role`, which invited `BrainPanel.ts`'s cadence
  // lookup to pass the wrong field entirely; see `format/cadence.ts`).
  role: string,
  // A named options object (code review finding), not a bare positional
  // `sizing` argument threaded in between `role` and `nowMs` — a plain
  // `buildVitalsState(events, perf, curve, role, null, NOW)` call reads as
  // "what is this `null`?" at every call site; `{ nowMs: NOW }` is
  // self-documenting and additive (a future option never reshuffles every
  // existing positional call).
  options: BuildVitalsStateOptions = {},
): VitalsState {
  const { sizing = null, nowMs = Date.now(), unitState = null, freshEpisode = false } = options;
  const base = buildSummaryState(events, performance);
  // Only a unit systemd reports as NOT running is "stopped"; "activating" (a start or
  // a Restart= backoff) and an unknown state fall back to the event evidence.
  const unit = (unitState ?? "").trim().toLowerCase();
  const stopped = unit === "inactive" || unit === "failed" || unit === "deactivating";
  return {
    ...base,
    status: stopped ? "stopped" : computeStatus(base, events, role, nowMs),
    freshEpisode,
    // A fresh episode's book is flat by construction (fresh_start.sh verified the
    // account flat before writing the genesis record): 0, not "unknown".
    openPositions: base.openPositions ?? (freshEpisode ? 0 : null),
    openOrders: base.openOrders ?? (freshEpisode ? 0 : null),
    navSeries: equityCurve
      .map((row) => (row.nav !== null ? Number.parseFloat(row.nav) : Number.NaN))
      .filter((n) => Number.isFinite(n)),
    dayPnl: computeDayPnl(equityCurve),
    grossExposure: sizing?.gross_current_weight ?? null,
    grossExposureCap: sizing?.gross_cap_weight ?? null,
  };
}
